import { Config } from 'App/config';

import { getCurrentAccount, PostSessionResponse, useMutateData, usePostSession } from '@ecolytiq/js-data-provider';

import { useNavigate } from 'react-router-dom';

import { useCallback, useContext, useEffect } from 'react';

import {
  setIsFirstLoginAction,
  setIsLoggedInAction,
  setSessionTokenAction,
  TCContext,
  useDeps,
  useUserContext,
} from 'App/context';

import { useCookies } from 'react-cookie';

import { RoutePaths } from 'impactApp/routes/routePaths';
import { appConfig } from 'App/appConfig/appConfig';

import { handleUserLogin } from '../../../App/utils';

export const useSessionData = (code: string, state: string, codeVerifier: string) => {
  const { httpClientService, httpClientServiceOAuth } = useDeps();
  const [{ sessionToken }, dispatch] = useUserContext();
  const {
    REACT_APP_DEMO_API,
    REACT_APP_MOCK_API,
    REACT_APP_ECOLYTIQ_API_AUTH_CLIENT_ID,
    REACT_APP_ECOLYTIQ_API_AUTH_CLIENT_SECRET,
  } = Config.getAll();
  const navigate = useNavigate();
  const { PROFILE_SETTINGS, HOME, SSO_PAGE } = RoutePaths;
  const { isTCOpen } = useContext(TCContext);
  const { REACT_APP_FIRST_LOGIN_COOKIE_NAME, REACT_APP_ACCESS_TOKEN_COOKIE_NAME, REACT_APP_REFRESH_TOKEN_COOKIE_NAME } =
    appConfig;
  const [cookies, setCookie] = useCookies([REACT_APP_FIRST_LOGIN_COOKIE_NAME]);

  const loginAndRedirectToHomePage = useCallback(() => {
    dispatch(setIsLoggedInAction(true));
    navigate(HOME);
  }, [HOME, dispatch, navigate]);

  const { mutate: sessionMutate } = useMutateData<PostSessionResponse>(
    usePostSession(httpClientServiceOAuth, code, codeVerifier),
    {
      onSuccess: async (sessionDataResponse: any) => {
        const { access_token, refresh_token } = sessionDataResponse;
        dispatch(setSessionTokenAction(access_token));
        handleUserLogin(access_token);
        const interceptor = httpClientService.setInterceptor(`Bearer ${access_token}`);
        setCookie(REACT_APP_ACCESS_TOKEN_COOKIE_NAME, access_token, {
          path: '/',
        });

        setCookie(REACT_APP_REFRESH_TOKEN_COOKIE_NAME, refresh_token, { path: '/' });

        const firstLoginFunction = getCurrentAccount(httpClientService);
        const account = await firstLoginFunction.get();
        const { onboardingAnswers } = account.data.currentAccount;
        dispatch(setIsLoggedInAction(true));
        const answeredOnboardingAnswers = onboardingAnswers.filter((question: any) => question.answerId != null);
        if (cookies[REACT_APP_FIRST_LOGIN_COOKIE_NAME] === 'true' || answeredOnboardingAnswers.length > 0) {
          navigate(HOME);
        } else {
          dispatch(setIsFirstLoginAction(true));
          setCookie(REACT_APP_FIRST_LOGIN_COOKIE_NAME, 'true');
          navigate(PROFILE_SETTINGS);
        }
        httpClientService.removeInterceptor(interceptor);
      },
      onError: async () => {
        navigate(SSO_PAGE);
      },
    }
  );

  useEffect(() => {
    if (REACT_APP_MOCK_API === 'true') loginAndRedirectToHomePage();
    else if (REACT_APP_DEMO_API === 'true' && !code) navigate(`./?code=demo-account`);
    else if (!sessionToken && code && isTCOpen) {
      httpClientServiceOAuth.removeInterceptor(0);
      httpClientServiceOAuth.setInterceptor(({ headers, ...restRequestConfig }: any) => {
        return {
          ...restRequestConfig,
          headers: {
            ...headers,
            authorization: `Basic ${Buffer.from(
              `${REACT_APP_ECOLYTIQ_API_AUTH_CLIENT_ID}:${REACT_APP_ECOLYTIQ_API_AUTH_CLIENT_SECRET}`
            ).toString('base64')}`,
          },
        };
      });
      sessionMutate();
    }
  }, [
    sessionMutate,
    sessionToken,
    code,
    navigate,
    state,
    PROFILE_SETTINGS,
    REACT_APP_DEMO_API,
    isTCOpen,
    REACT_APP_MOCK_API,
    loginAndRedirectToHomePage,
    REACT_APP_ECOLYTIQ_API_AUTH_CLIENT_ID,
    REACT_APP_ECOLYTIQ_API_AUTH_CLIENT_SECRET,
    httpClientService,
    httpClientServiceOAuth,
  ]);
};
